/**
 * Words for people, codes for agents (ADR-0140). What the platform stores
 * and sends is an exact code — `waiting_reply`, `SE`, `hard_bounce_rate` —
 * and every screen a person reads says it in words, from here. Nothing
 * here changes what goes over the wire.
 */

let regions: Intl.DisplayNames | null = null;
let languages: Intl.DisplayNames | null = null;

/** A country by its name: `SE` → "Sweden". An unknown code stays as it came. */
export function countryName(code: string | null | undefined): string {
  if (!code) return '—';
  try {
    regions ??= new Intl.DisplayNames(['en-GB'], { type: 'region' });
    return regions.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

export function countryNames(codes: readonly string[]): string {
  return codes.map(countryName).join(', ');
}

/** A language by its name: `da` → "Danish". */
export function languageName(code: string | null | undefined): string {
  if (!code) return '—';
  try {
    languages ??= new Intl.DisplayNames(['en-GB'], { type: 'language' });
    return languages.of(code) ?? code;
  } catch {
    return code;
  }
}

/** Any code as words, sentence case: `waiting_reply` → "Waiting reply". */
export function humanise(code: string): string {
  const w = code.replace(/[._:]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\s+/g, ' ').trim().toLowerCase();
  return w ? w.charAt(0).toUpperCase() + w.slice(1) : '—';
}

// The states and outcomes the platform's records pass through, said the way
// a person would say them. Anything not here is humanised.
const STATE_WORDS: Record<string, string> = {
  open: 'Open',
  opened: 'Open',
  in_progress: 'In progress',
  waiting: 'Waiting',
  waiting_reply: 'Waiting for their reply',
  waiting_us: 'Waiting for us',
  snoozed: 'Snoozed',
  closed: 'Closed',
  done: 'Done',
  confirmed: 'Done',
  ok: 'Done',
  denied: 'Refused',
  refused: 'Refused',
  error: 'Failed',
  failed: 'Failed',
  skipped: 'Skipped',
  cancelled: 'Cancelled',
  accepted: 'Taken on',
  acknowledged: 'Seen',
  completed: 'Done',
  declined: 'Declined',
  tentative: 'Maybe',
  needsAction: 'Not answered yet',
  sent_unchanged: 'Sent as drafted',
  sent_edited: 'Sent with edits',
  discarded: 'Discarded',
  prepared: 'Ready to send',
  sent: 'Sent',
  draft: 'Draft',
  approved: 'Approved',
  ended: 'Ended',
  running: 'Running',
  live: 'Live',
  proposed: 'Proposed',
  retired: 'Retired',
  active: 'Active',
  paused: 'Paused',
  suspended: 'Stopped',
  pilot: 'Pilot',
  made: 'Made',
  asked: 'Asked',
  no_order: 'No order',
  ordered: 'Ordered',
  set: 'Key set',
  rotate: 'Key changed',
  rotated: 'Key changed',
  revoke: 'Key removed',
  revoked: 'Key removed',
  upload: 'Uploaded',
  drive: 'From Drive',
  routine: 'Routine',
  sensitive: 'Needs care',
  critical: 'Needs a person’s code',
};

/** A record's state or outcome in words. */
export function stateWords(state: string | null | undefined): string {
  if (!state) return '—';
  return STATE_WORDS[state] ?? humanise(state);
}

// What the platform measures and stops on (ADR-0086, the rollback triggers).
const METRIC_WORDS: Record<string, string> = {
  complaint_rate: 'Complaints',
  hard_bounce_rate: 'Addresses that bounced',
  error_rate: 'Errors',
  order_error_rate: 'Orders that went wrong',
  late_dispatch_rate: 'Late dispatches',
  unsubscribed: 'Unsubscribes',
  opened: 'Opens',
  clicked: 'Clicks',
};

export function metricWords(metric: string): string {
  return METRIC_WORDS[metric] ?? humanise(metric);
}

// What a signal says happened at a place (ADR-0141).
const SIGNAL_WORDS: Record<string, string> = {
  opening: 'Opening',
  closure: 'Closing',
  chef_move: 'A chef moved',
  menu_mention: 'On the menu',
  already_buys: 'Already buys it',
  fair: 'At a fair',
};

export function signalWords(kind: string): string {
  return SIGNAL_WORDS[kind] ?? humanise(kind);
}

// Where a prospect came from.
const SOURCE_WORDS: Record<string, string> = {
  places: 'Found on Google',
  base_list: 'From a list we imported',
  research: 'From research',
};

export function prospectSourceWords(source: string): string {
  return SOURCE_WORDS[source] ?? humanise(source);
}

/** A calendar day as a person writes it: `2026-12-26` → "26 December 2026". */
export function dayWords(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
}

// A person's roles, by what they are.
const ROLE_WORDS: Record<string, string> = { 'platform.cto': 'CTO', 'platform.cpo': 'CPO', 'platform.superadmin': 'Superadmin' };

/** A permission in words: `outbound.research` → "Outbound: research". */
export function scopeWords(scope: string): string {
  if (ROLE_WORDS[scope]) return ROLE_WORDS[scope];
  const [dept = '', what = ''] = scope.split('.');
  if (!what) return humanise(scope);
  return `${humanise(dept)}: ${what === '*' ? 'everything' : humanise(what).toLowerCase()}`;
}

/** An agent's handle as its name: `otis` → "Otis", `sol.twin` → "Sol, a twin". */
export function handleWords(handle: string): string {
  const [given = handle, kind] = handle.split('@')[0]!.split('.');
  const name = given.charAt(0).toUpperCase() + given.slice(1);
  return kind === 'twin' ? `${name}, a twin` : name;
}

/**
 * Why a request was refused, in words: the router's own reasons are codes,
 * and a person's refusal is already their words, so it passes through.
 */
export function refusalReasonWords(reason: string): string {
  if (reason === 'no_agent_no_lead') return 'it has no agent for this and no lead to take it, so nobody there could';
  return reason;
}
